import type { Conflict } from '../types';

export const conflicts: Conflict[] = [
  // ═══════════════════════════════════════════
  // MAJOR WARS (10,000+ annual deaths)
  // ═══════════════════════════════════════════
  {
    id: 'ukraine-russia',
    name: 'Russo-Ukrainian War',
    countries: ['UKR', 'RUS', 'BLR'],
    countryNames: ['Ukraine', 'Russia', 'Belarus'],
    category: 'major_war',
    type: 'state-based',
    startYear: 2014,
    deathsCumulative: 500000,
    deaths2024: 80000,
    deaths2025: 80205,
    sideA: ['Ukraine', 'Western military aid'],
    sideB: ['Russia', 'Wagner Group (disbanded)', 'Belarusian support'],
    region: 'Europe',
    description: 'Full-scale interstate war following Russia\'s 2022 invasion of Ukraine. The deadliest conflict in Europe since WWII, with massive artillery warfare, drone strikes, and trench combat across a 1,000km front line.',
    status: 'Active — intense frontline combat, drone/missile strikes on cities',
  },
  {
    id: 'israel-palestine',
    name: 'Israel–Palestine & Regional War',
    countries: ['ISR', 'PSE', 'LBN', 'SYR', 'IRN', 'YEM', 'IRQ'],
    countryNames: ['Israel', 'Palestine', 'Lebanon', 'Syria', 'Iran', 'Yemen', 'Iraq'],
    category: 'major_war',
    type: 'mixed',
    startYear: 1948,
    deathsCumulative: 260000,
    deaths2024: 40000,
    deaths2025: 28111,
    sideA: ['Israel', 'IDF'],
    sideB: ['Hamas', 'Hezbollah', 'Houthis', 'Iranian-backed militias', 'PIJ'],
    region: 'Middle East',
    description: 'The October 2023 Hamas attack and subsequent Israeli military operation in Gaza escalated into a regional conflict involving Hezbollah in Lebanon, Houthi attacks in the Red Sea, and Iranian missile strikes. Massive civilian casualties in Gaza.',
    status: 'Active — Gaza operations, regional escalation with Iran/Hezbollah',
  },
  {
    id: 'myanmar-civil-war',
    name: 'Myanmar Civil War',
    countries: ['MMR', 'IND', 'BGD', 'THA', 'CHN'],
    countryNames: ['Myanmar', 'India', 'Bangladesh', 'Thailand', 'China'],
    category: 'major_war',
    type: 'state-based',
    startYear: 1948,
    deathsCumulative: 214000,
    deaths2024: 12000,
    deaths2025: 15699,
    sideA: ['Myanmar military junta (Tatmadaw)'],
    sideB: ['NUG (National Unity Government)', 'Ethnic Armed Organizations', 'People\'s Defence Forces', 'Kachin Independence Army', 'Karen National Union'],
    region: 'Asia',
    description: 'The world\'s longest-running civil war, dramatically escalated after the 2021 military coup. Ethnic armed organisations and pro-democracy forces now control significant territory, challenging the junta\'s grip on power.',
    status: 'Active — junta losing territory to resistance coalition',
  },
  {
    id: 'sudan-civil-war',
    name: 'Sudanese Civil War',
    countries: ['SDN', 'SSD', 'TCD', 'CAF', 'EGY', 'ETH', 'LBY'],
    countryNames: ['Sudan', 'South Sudan', 'Chad', 'Central African Republic', 'Egypt', 'Ethiopia', 'Libya'],
    category: 'major_war',
    type: 'state-based',
    startYear: 2023,
    deathsCumulative: 150000,
    deaths2024: 50000,
    deaths2025: 40000,
    sideA: ['Sudanese Armed Forces (SAF)'],
    sideB: ['Rapid Support Forces (RSF)', 'Allied militias'],
    region: 'Africa',
    description: 'Civil war between the Sudanese military and the paramilitary RSF that erupted in April 2023. Has caused one of the world\'s worst humanitarian crises, with millions displaced and widespread ethnic violence in Darfur.',
    status: 'Active — territorial consolidation by both sides, humanitarian catastrophe',
  },
  {
    id: 'drc-conflicts',
    name: 'Congolese Conflicts',
    countries: ['COD', 'CAF', 'SSD', 'RWA', 'BDI', 'UGA'],
    countryNames: ['DR Congo', 'Central African Republic', 'South Sudan', 'Rwanda', 'Burundi', 'Uganda'],
    category: 'major_war',
    type: 'mixed',
    startYear: 1996,
    deathsCumulative: 220000,
    deaths2024: 8000,
    deaths2025: 10000,
    sideA: ['DRC Armed Forces (FARDC)', 'MONUSCO (UN)'],
    sideB: ['M23', 'ADF/ISIS-DRC', '100+ armed groups', 'Rwandan support to M23'],
    region: 'Africa',
    description: 'Complex, overlapping conflicts in eastern DRC driven by competition for critical minerals, ethnic tensions, and regional power struggles. Rwanda-backed M23 rebels have seized significant territory in North Kivu.',
    status: 'Active — M23 offensive in North Kivu, ADF attacks in Ituri',
  },
  {
    id: 'somalia-civil-war',
    name: 'Somali Civil War',
    countries: ['SOM', 'KEN'],
    countryNames: ['Somalia', 'Kenya'],
    category: 'major_war',
    type: 'mixed',
    startYear: 1991,
    deathsCumulative: 500000,
    deaths2024: 10000,
    deaths2025: 10783,
    sideA: ['Federal Government of Somalia', 'ATMIS (African Union)', 'US military'],
    sideB: ['Al-Shabaab', 'ISIS-Somalia', 'Clan militias'],
    region: 'Africa',
    description: 'Decades-long conflict marked by state collapse, clan warfare, and jihadist insurgency. Al-Shabaab controls rural areas and conducts devastating bombings. AU peacekeepers are drawing down, raising concerns of a security vacuum.',
    status: 'Active — al-Shabaab controls rural areas, ATMIS drawdown',
  },
  {
    id: 'sahel-insurgency',
    name: 'Sahel Islamist Insurgency',
    countries: ['MLI', 'BFA', 'NER', 'NGA', 'TCD', 'CMR', 'BEN', 'TGO', 'CIV'],
    countryNames: ['Mali', 'Burkina Faso', 'Niger', 'Nigeria', 'Chad', 'Cameroon', 'Benin', 'Togo', 'Ivory Coast'],
    category: 'major_war',
    type: 'mixed',
    startYear: 2002,
    deathsCumulative: 485500,
    deaths2024: 20000,
    deaths2025: 21185,
    sideA: ['National armies', 'Wagner Group/Africa Corps (Russia)'],
    sideB: ['JNIM (al-Qaeda affiliate)', 'ISSP/ISWAP (ISIS affiliates)', 'Boko Haram', 'Local bandits'],
    region: 'Africa',
    description: 'The Sahel has become the global epicenter of terrorism. Following military coups in Mali, Niger, and Burkina Faso that expelled Western forces, jihadist groups linked to al-Qaeda and ISIS have rapidly expanded control. Boko Haram continues devastating Nigeria\'s northeast.',
    status: 'Active — jihadist expansion, military junta rule, Western forces expelled',
  },
  {
    id: 'mexico-drug-war',
    name: 'Mexican Drug War',
    countries: ['MEX', 'BLZ', 'SLV', 'HND', 'GTM', 'NIC'],
    countryNames: ['Mexico', 'Belize', 'El Salvador', 'Honduras', 'Guatemala', 'Nicaragua'],
    category: 'major_war',
    type: 'non-state',
    startYear: 2006,
    deathsCumulative: 400000,
    deaths2024: 10000,
    deaths2025: 10767,
    sideA: ['Mexican government forces', 'National Guard'],
    sideB: ['Sinaloa Cartel', 'CJNG', 'Gulf Cartel', 'Los Zetas remnants', 'Local gangs'],
    region: 'Americas',
    description: 'Ongoing war between the Mexican government and drug cartels, and between rival cartels themselves. Extreme violence including mass kidnappings, assassinations, and territorial battles over drug trafficking routes to the US.',
    status: 'Active — cartel infighting, government military deployments',
  },

  // ═══════════════════════════════════════════
  // MINOR WARS (1,000–9,999 annual deaths)
  // ═══════════════════════════════════════════
  {
    id: 'ethiopia-civil',
    name: 'Ethiopian Civil Conflict',
    countries: ['ETH'],
    countryNames: ['Ethiopia'],
    category: 'minor_war',
    type: 'state-based',
    startYear: 2018,
    deathsCumulative: 600000,
    deaths2024: 6000,
    deaths2025: 6316,
    sideA: ['Ethiopian National Defense Force', 'Eritrean forces'],
    sideB: ['Fano militia (Amhara)', 'Oromo Liberation Army', 'TPLF remnants'],
    region: 'Africa',
    description: 'After the Tigray war ceasefire in 2022, new fronts opened with Amhara Fano militants and Oromo rebels. Fano fighters oppose the disarmament of regional forces, while OLA continues insurgency in Oromia.',
    status: 'Active — Amhara Fano insurgency, Oromo rebellion',
  },
  {
    id: 'syria-conflict',
    name: 'Syrian Conflict',
    countries: ['SYR', 'ISR', 'LBN', 'IRQ', 'TUR'],
    countryNames: ['Syria', 'Israel', 'Lebanon', 'Iraq', 'Turkey'],
    category: 'minor_war',
    type: 'mixed',
    startYear: 2011,
    deathsCumulative: 665000,
    deaths2024: 8000,
    deaths2025: 8400,
    sideA: ['HTS (de facto government)', 'Syrian National Army'],
    sideB: ['SDF/Kurdish forces', 'ISIS remnants', 'Pro-Assad remnants'],
    region: 'Middle East',
    description: 'The fall of Assad in late 2024 transformed the conflict landscape. HTS (formerly al-Nusra) leads the transitional government. Turkey continues operations against Kurdish forces. Israel occupies buffer zones. ISIS cells remain active.',
    status: 'Active — post-Assad transition, Turkish operations, Kurdish resistance',
  },
  {
    id: 'yemen-civil-war',
    name: 'Yemeni Civil War',
    countries: ['YEM', 'SAU', 'ARE'],
    countryNames: ['Yemen', 'Saudi Arabia', 'UAE'],
    category: 'minor_war',
    type: 'state-based',
    startYear: 2014,
    deathsCumulative: 385000,
    deaths2024: 3000,
    deaths2025: 2895,
    sideA: ['Internationally recognized government', 'Saudi-led coalition', 'Southern Transitional Council'],
    sideB: ['Ansar Allah (Houthis)'],
    region: 'Middle East',
    description: 'Civil war between the Houthi movement and the internationally recognized government, complicated by Saudi/UAE intervention. Houthi Red Sea attacks on shipping since 2023 drew US/UK military strikes.',
    status: 'Active — Houthi Red Sea attacks, US strikes, internal divisions',
  },
  {
    id: 'colombia-conflict',
    name: 'Colombian Conflict',
    countries: ['COL', 'VEN', 'ECU'],
    countryNames: ['Colombia', 'Venezuela', 'Ecuador'],
    category: 'minor_war',
    type: 'mixed',
    startYear: 1964,
    deathsCumulative: 460000,
    deaths2024: 2500,
    deaths2025: 2728,
    sideA: ['Colombian Armed Forces'],
    sideB: ['ELN', 'FARC dissidents (EMC)', 'Gulf Clan', 'Drug trafficking groups'],
    region: 'Americas',
    description: 'Despite the 2016 FARC peace deal, armed groups continue fighting over drug routes and territory. ELN guerrillas, FARC dissident factions, and criminal organisations wage violence in remote regions.',
    status: 'Active — ELN peace talks stalled, FARC dissident operations',
  },
  {
    id: 'afghanistan-conflict',
    name: 'Afghan Conflict',
    countries: ['AFG', 'PAK', 'TJK'],
    countryNames: ['Afghanistan', 'Pakistan', 'Tajikistan'],
    category: 'minor_war',
    type: 'mixed',
    startYear: 1978,
    deathsCumulative: 2000000,
    deaths2024: 4000,
    deaths2025: 4409,
    sideA: ['Taliban government'],
    sideB: ['ISIS-Khorasan', 'National Resistance Front', 'ETIM'],
    region: 'Asia',
    description: 'Since the Taliban takeover in 2021, the conflict shifted to an ISIS-K insurgency targeting the Taliban government, plus sporadic resistance from NRF fighters. ISIS-K conducts devastating bombings against religious minorities.',
    status: 'Active — ISIS-K insurgency, resistance cells, border clashes with Pakistan',
  },
  {
    id: 'haiti-crisis',
    name: 'Haitian Crisis',
    countries: ['HTI'],
    countryNames: ['Haiti'],
    category: 'minor_war',
    type: 'non-state',
    startYear: 2018,
    deathsCumulative: 20000,
    deaths2024: 5000,
    deaths2025: 3000,
    sideA: ['Haitian National Police', 'MSS (Multinational Security Support)'],
    sideB: ['Gang coalitions (Viv Ansanm, G-Pep)', 'Armed gangs'],
    region: 'Americas',
    description: 'Haiti has descended into near-total state collapse as powerful gangs control most of Port-au-Prince and major highways. Gang violence, kidnappings, and territorial battles have displaced hundreds of thousands.',
    status: 'Active — gang control of Port-au-Prince, MSS deployment',
  },
  {
    id: 'ecuador-conflict',
    name: 'Ecuadorian Conflict',
    countries: ['ECU', 'COL'],
    countryNames: ['Ecuador', 'Colombia'],
    category: 'minor_war',
    type: 'non-state',
    startYear: 2018,
    deathsCumulative: 13000,
    deaths2024: 4000,
    deaths2025: 4051,
    sideA: ['Ecuadorian Armed Forces', 'National Police'],
    sideB: ['Los Choneros', 'Los Lobos', 'R7', 'Colombian cartel affiliates'],
    region: 'Americas',
    description: 'Ecuador declared a state of internal armed conflict in 2024 as drug trafficking gangs seized control of prisons and carried out brazen attacks. Violence linked to cocaine transit routes from Colombia.',
    status: 'Active — state of emergency, military operations against gangs',
  },
  {
    id: 'car-civil-war',
    name: 'Central African Republic Civil War',
    countries: ['CAF', 'CMR', 'COD', 'SSD'],
    countryNames: ['Central African Republic', 'Cameroon', 'DR Congo', 'South Sudan'],
    category: 'minor_war',
    type: 'state-based',
    startYear: 2012,
    deathsCumulative: 16300,
    deaths2024: 1200,
    deaths2025: 1215,
    sideA: ['CAR government', 'Wagner Group/Africa Corps (Russia)', 'MINUSCA (UN)'],
    sideB: ['CPC rebel coalition', 'Anti-balaka militias', '3R', 'UPC'],
    region: 'Africa',
    description: 'Ongoing civil war between government forces (supported by Russian mercenaries) and a coalition of rebel groups. Widespread human rights abuses by all sides, including Russian mercenary forces.',
    status: 'Active — rebel activity in provinces, Russian mercenary presence',
  },
  {
    id: 'cameroon-conflicts',
    name: 'Cameroonian Conflicts',
    countries: ['CMR', 'NGA', 'NER', 'TCD'],
    countryNames: ['Cameroon', 'Nigeria', 'Niger', 'Chad'],
    category: 'minor_war',
    type: 'mixed',
    startYear: 2016,
    deathsCumulative: 11000,
    deaths2024: 1800,
    deaths2025: 1826,
    sideA: ['Cameroonian military'],
    sideB: ['Anglophone separatists (Ambazonia)', 'Boko Haram'],
    region: 'Africa',
    description: 'Two simultaneous conflicts: the Anglophone Crisis in western Cameroon where English-speaking separatists fight for independence (Ambazonia), and Boko Haram incursions in the Far North region.',
    status: 'Active — Anglophone separatist attacks, Boko Haram raids',
  },
  {
    id: 'iran-insurgencies',
    name: 'Iranian Insurgencies',
    countries: ['IRN', 'IRQ', 'TUR'],
    countryNames: ['Iran', 'Iraq', 'Turkey'],
    category: 'minor_war',
    type: 'state-based',
    startYear: 1979,
    deathsCumulative: 48000,
    deaths2024: 500,
    deaths2025: 1000,
    sideA: ['Islamic Revolutionary Guard Corps (IRGC)', 'Iranian military'],
    sideB: ['PJAK (Kurdish)', 'Jaish ul-Adl (Baloch)', 'Arab separatists', 'ISIS cells'],
    region: 'Middle East',
    description: 'Multiple insurgencies along Iran\'s borders — Kurdish separatists in the northwest, Baloch militants in the southeast, and Arab separatists in Khuzestan. Escalated significantly amid regional tensions.',
    status: 'Active — border clashes, militant attacks in border provinces',
  },
  {
    id: 'venezuela-conflict',
    name: 'Venezuelan Crisis',
    countries: ['VEN', 'COL'],
    countryNames: ['Venezuela', 'Colombia'],
    category: 'minor_war',
    type: 'mixed',
    startYear: 2010,
    deathsCumulative: 100000,
    deaths2024: 2500,
    deaths2025: 2602,
    sideA: ['Venezuelan government forces', 'FAES'],
    sideB: ['Opposition groups', 'Colombian armed groups', 'Criminal gangs', 'TREN de Aragua'],
    region: 'Americas',
    description: 'Political violence, state repression, and criminal gang warfare in a context of economic collapse. TREN de Aragua has expanded into a transnational criminal organisation. Extrajudicial killings by security forces.',
    status: 'Active — political repression, gang warfare, cross-border violence',
  },

  // ═══════════════════════════════════════════
  // CONFLICTS (100–999 annual deaths)
  // ═══════════════════════════════════════════
  {
    id: 'pakistan-insurgencies',
    name: 'Pakistani Insurgencies',
    countries: ['PAK', 'AFG', 'IRN'],
    countryNames: ['Pakistan', 'Afghanistan', 'Iran'],
    category: 'conflict',
    type: 'mixed',
    startYear: 2004,
    deathsCumulative: 80000,
    deaths2024: 900,
    deaths2025: 985,
    sideA: ['Pakistan Armed Forces'],
    sideB: ['TTP (Tehrik-i-Taliban Pakistan)', 'BLA (Balochistan Liberation Army)', 'ISIS-K cells'],
    region: 'Asia',
    description: 'Worsening Islamist and separatist insurgencies in Pakistan\'s peripheries. TTP attacks in Khyber Pakhtunkhwa, Baloch separatist bombings, and Afghan-Pakistani border clashes have all intensified.',
    status: 'Active — TTP attacks escalating, Baloch separatism rising',
  },
  {
    id: 'india-kashmir',
    name: 'Kashmir & Indo-Pakistani Tensions',
    countries: ['IND', 'PAK', 'CHN'],
    countryNames: ['India', 'Pakistan', 'China'],
    category: 'conflict',
    type: 'state-based',
    startYear: 1947,
    deathsCumulative: 100000,
    deaths2024: 500,
    deaths2025: 975,
    sideA: ['Indian security forces'],
    sideB: ['Kashmiri militants', 'Pakistan-backed groups', 'Jaish-e-Mohammed'],
    region: 'Asia',
    description: 'Long-running conflict over the disputed Kashmir region. India and Pakistan have fought multiple wars over it. Low-level insurgency continues with periodic escalation, including the 2025 Pahalgam attack and subsequent India-Pakistan military crisis.',
    status: 'Active — insurgent attacks, India-Pakistan military standoff',
  },
  {
    id: 'india-insurgencies',
    name: 'Indian Insurgencies',
    countries: ['IND', 'MMR', 'BTN'],
    countryNames: ['India', 'Myanmar', 'Bhutan'],
    category: 'conflict',
    type: 'mixed',
    startYear: 1954,
    deathsCumulative: 81400,
    deaths2024: 800,
    deaths2025: 980,
    sideA: ['Indian security forces'],
    sideB: ['Naxalite-Maoist insurgents', 'Northeast armed groups (NSCN, ULFA)', 'Manipur ethnic militias'],
    region: 'Asia',
    description: 'Multiple low-intensity conflicts across India — the Maoist/Naxalite insurgency in central-eastern states (Red Corridor), ethnic insurgencies in the Northeast (Nagaland, Manipur, Assam), and communal violence.',
    status: 'Active — Naxalite operations declining, Manipur ethnic violence',
  },
  {
    id: 'nigeria-civil',
    name: 'Nigerian Communal Conflicts',
    countries: ['NGA'],
    countryNames: ['Nigeria'],
    category: 'conflict',
    type: 'non-state',
    startYear: 1998,
    deathsCumulative: 80000,
    deaths2024: 3000,
    deaths2025: 3200,
    sideA: ['Farming communities', 'State security forces'],
    sideB: ['Fulani herders', 'Bandits', 'Militant groups'],
    region: 'Africa',
    description: 'Deadly communal violence between farming and herding communities in Nigeria\'s Middle Belt, compounded by banditry in the northwest. Driven by climate change, land disputes, and ethnic tensions.',
    status: 'Active — herder-farmer clashes, banditry in northwest',
  },
  {
    id: 'iraq-conflict',
    name: 'Iraqi Conflict',
    countries: ['IRQ'],
    countryNames: ['Iraq'],
    category: 'conflict',
    type: 'mixed',
    startYear: 2003,
    deathsCumulative: 500000,
    deaths2024: 500,
    deaths2025: 574,
    sideA: ['Iraqi government', 'Kurdish Peshmerga'],
    sideB: ['ISIS cells', 'Iran-backed militias (PMF)'],
    region: 'Middle East',
    description: 'Residual ISIS insurgency in rural areas with periodic sleeper cell attacks. Iranian-backed Popular Mobilisation Forces operate semi-independently. Kurdish tensions over territory and oil revenues.',
    status: 'Active — ISIS sleeper cells, militia activity, political tensions',
  },
  {
    id: 'mozambique-insurgency',
    name: 'Mozambique Insurgency',
    countries: ['MOZ', 'TZA'],
    countryNames: ['Mozambique', 'Tanzania'],
    category: 'conflict',
    type: 'state-based',
    startYear: 2017,
    deathsCumulative: 7800,
    deaths2024: 800,
    deaths2025: 824,
    sideA: ['Mozambican military', 'SADC forces', 'Rwandan forces'],
    sideB: ['ASWJ/ISIS-Mozambique (al-Shabaab Mozambique)'],
    region: 'Africa',
    description: 'ISIS-linked insurgency in Cabo Delgado province threatening major natural gas projects. Rwandan and Southern African forces deployed to support the government.',
    status: 'Active — insurgent attacks in Cabo Delgado, foreign troop deployment',
  },
  {
    id: 'philippines-conflict',
    name: 'Philippine Insurgencies',
    countries: ['PHL'],
    countryNames: ['Philippines'],
    category: 'conflict',
    type: 'mixed',
    startYear: 1969,
    deathsCumulative: 200000,
    deaths2024: 500,
    deaths2025: 536,
    sideA: ['Philippine Armed Forces'],
    sideB: ['New People\'s Army (NPA/CPP)', 'ASG remnants', 'ISIS-East Asia affiliates'],
    region: 'Asia',
    description: 'Long-running communist insurgency by the NPA, plus residual jihadist activity in the southern Philippines (Mindanao). The drug war under previous administrations added another dimension of violence.',
    status: 'Active — NPA weakened but active, Mindanao islamist remnants',
  },
  {
    id: 'libya-crisis',
    name: 'Libyan Crisis',
    countries: ['LBY'],
    countryNames: ['Libya'],
    category: 'conflict',
    type: 'mixed',
    startYear: 2011,
    deathsCumulative: 40000,
    deaths2024: 300,
    deaths2025: 282,
    sideA: ['Government of National Unity (Tripoli)'],
    sideB: ['Libyan National Army (Haftar, east)', 'Various militias', 'ISIS cells'],
    region: 'Africa',
    description: 'Political fragmentation between rival governments in Tripoli and Benghazi. Militia violence, human trafficking networks, and periodic clashes. A transit point for migration to Europe.',
    status: 'Active — dual government standoff, militia clashes, migration crisis',
  },
  {
    id: 'papua-conflict',
    name: 'Papua Conflict',
    countries: ['IDN'],
    countryNames: ['Indonesia'],
    category: 'conflict',
    type: 'state-based',
    startYear: 1962,
    deathsCumulative: 200000,
    deaths2024: 250,
    deaths2025: 255,
    sideA: ['Indonesian military (TNI)'],
    sideB: ['West Papua National Liberation Army (TPNPB/OPM)'],
    region: 'Asia',
    description: 'Long-running separatist insurgency in Indonesia\'s Papua provinces. The Free Papua Movement seeks independence, while Indonesia maintains tight military control. International attention limited.',
    status: 'Active — separatist attacks, military operations, media blackout',
  },
  {
    id: 'bangladesh-insurgencies',
    name: 'Bangladeshi Insurgencies',
    countries: ['BGD', 'MMR', 'IND'],
    countryNames: ['Bangladesh', 'Myanmar', 'India'],
    category: 'conflict',
    type: 'mixed',
    startYear: 1999,
    deathsCumulative: 10000,
    deaths2024: 350,
    deaths2025: 368,
    sideA: ['Bangladesh security forces'],
    sideB: ['Chittagong Hill Tracts insurgents', 'Islamist militants', 'Political violence'],
    region: 'Asia',
    description: 'Ethnic tensions in the Chittagong Hill Tracts, Islamist militant activity, and political violence between rival parties. The Rohingya refugee crisis adds cross-border complexity.',
    status: 'Active — political violence, CHT tensions, Rohingya border issues',
  },
  {
    id: 'kurdish-conflicts',
    name: 'Kurdish–Turkish Conflict',
    countries: ['TUR', 'SYR', 'IRQ'],
    countryNames: ['Turkey', 'Syria', 'Iraq'],
    category: 'conflict',
    type: 'state-based',
    startYear: 1984,
    deathsCumulative: 140000,
    deaths2024: 300,
    deaths2025: 300,
    sideA: ['Turkish Armed Forces', 'Turkish-backed Syrian factions'],
    sideB: ['PKK', 'YPG/SDF (Syria)', 'PJAK (Iran)'],
    region: 'Middle East',
    description: 'Decades-long conflict between Turkey and Kurdish militant groups, primarily the PKK. Turkey conducts cross-border operations into Iraq and Syria targeting Kurdish positions.',
    status: 'Active — Turkish operations in Iraq/Syria, PKK attacks',
  },
  {
    id: 'angola-cabinda',
    name: 'Cabinda War',
    countries: ['AGO'],
    countryNames: ['Angola'],
    category: 'conflict',
    type: 'state-based',
    startYear: 1975,
    deathsCumulative: 30000,
    deaths2024: 200,
    deaths2025: 200,
    sideA: ['Angolan military'],
    sideB: ['FLEC (Front for the Liberation of the Enclave of Cabinda)'],
    region: 'Africa',
    description: 'Low-intensity separatist insurgency in the oil-rich Cabinda exclave. FLEC seeks independence for Cabinda, which is geographically separated from the rest of Angola.',
    status: 'Active — sporadic attacks on oil infrastructure and military',
  },
  {
    id: 'png-violence',
    name: 'Papua New Guinea Ethnic Violence',
    countries: ['PNG'],
    countryNames: ['Papua New Guinea'],
    category: 'conflict',
    type: 'non-state',
    startYear: 1988,
    deathsCumulative: 15000,
    deaths2024: 150,
    deaths2025: 140,
    sideA: ['Various tribal groups'],
    sideB: ['Rival tribal groups'],
    region: 'Oceania',
    description: 'Chronic tribal and ethnic violence in the Highlands region, driven by land disputes, revenge killings, and political rivalries. Major tribal warfare incidents cause mass casualties.',
    status: 'Active — tribal warfare in Highlands, police overwhelmed',
  },
  {
    id: 'jamaica-violence',
    name: 'Jamaican Political Conflict',
    countries: ['JAM'],
    countryNames: ['Jamaica'],
    category: 'conflict',
    type: 'non-state',
    startYear: 1943,
    deathsCumulative: 25000,
    deaths2024: 290,
    deaths2025: 290,
    sideA: ['Jamaican security forces'],
    sideB: ['Gang factions', 'Lottery scam networks'],
    region: 'Americas',
    description: 'Entrenched gang violence with political connections. Jamaica has one of the highest murder rates in the world, driven by gang turf wars, extortion, and the lottery scam industry.',
    status: 'Active — high homicide rate, gang territorial violence',
  },
  {
    id: 'honduras-gangs',
    name: 'Honduran Gang Violence',
    countries: ['HND'],
    countryNames: ['Honduras'],
    category: 'conflict',
    type: 'non-state',
    startYear: 2010,
    deathsCumulative: 50000,
    deaths2024: 500,
    deaths2025: 561,
    sideA: ['Honduran security forces'],
    sideB: ['MS-13', 'Barrio 18', 'Drug trafficking organizations'],
    region: 'Americas',
    description: 'Severe gang violence and drug trafficking in Honduras, a key transit point for cocaine bound for the US. Despite crackdowns, gangs maintain significant territorial control.',
    status: 'Active — gang suppression operations, drug trafficking violence',
  },

  // ═══════════════════════════════════════════
  // SKIRMISHES (<100 annual deaths)
  // ═══════════════════════════════════════════
  {
    id: 'western-sahara',
    name: 'Western Sahara Conflict',
    countries: ['MAR', 'ESH'],
    countryNames: ['Morocco', 'Western Sahara (Sahrawi Republic)'],
    category: 'skirmish',
    type: 'state-based',
    startYear: 1975,
    deathsCumulative: 20000,
    deaths2024: 50,
    deaths2025: 54,
    sideA: ['Morocco'],
    sideB: ['Polisario Front (SADR)'],
    region: 'Africa',
    description: 'Frozen conflict over Western Sahara, occupied by Morocco since 1975. The Polisario Front resumed armed attacks in 2020 after the collapse of a 29-year ceasefire.',
    status: 'Low-intensity — sporadic clashes along the sand wall',
  },
  {
    id: 'egypt-sinai',
    name: 'Sinai Insurgency',
    countries: ['EGY'],
    countryNames: ['Egypt'],
    category: 'skirmish',
    type: 'state-based',
    startYear: 2011,
    deathsCumulative: 7000,
    deaths2024: 50,
    deaths2025: 53,
    sideA: ['Egyptian military'],
    sideB: ['ISIS-Sinai Province'],
    region: 'Africa',
    description: 'ISIS-affiliated militants in the Sinai Peninsula conducting attacks on military and police. Largely contained through massive military operations but not fully eliminated.',
    status: 'Low-intensity — sporadic militant attacks, heavy military presence',
  },
  {
    id: 'south-thailand',
    name: 'South Thailand Insurgency',
    countries: ['THA'],
    countryNames: ['Thailand'],
    category: 'skirmish',
    type: 'state-based',
    startYear: 2004,
    deathsCumulative: 7300,
    deaths2024: 50,
    deaths2025: 51,
    sideA: ['Royal Thai Army'],
    sideB: ['BRN (Barisan Revolusi Nasional)', 'Malay separatist groups'],
    region: 'Asia',
    description: 'Malay Muslim separatist insurgency in Thailand\'s three southernmost provinces (Pattani, Yala, Narathiwat). Low-level attacks continue despite ongoing peace talks.',
    status: 'Low-intensity — peace talks ongoing, sporadic bombings',
  },
  {
    id: 'nagorno-karabakh',
    name: 'Armenia–Azerbaijan Border Tensions',
    countries: ['ARM', 'AZE'],
    countryNames: ['Armenia', 'Azerbaijan'],
    category: 'skirmish',
    type: 'state-based',
    startYear: 1988,
    deathsCumulative: 45000,
    deaths2024: 10,
    deaths2025: 8,
    sideA: ['Armenia'],
    sideB: ['Azerbaijan'],
    region: 'Europe',
    description: 'After Azerbaijan recaptured Nagorno-Karabakh in 2023, triggering the exodus of 120,000 ethnic Armenians, periodic border clashes and tensions persist over unresolved delimitation.',
    status: 'Low-intensity — border tensions, peace treaty negotiations',
  },
  {
    id: 'north-caucasus',
    name: 'North Caucasus Insurgency',
    countries: ['RUS', 'AZE', 'GEO'],
    countryNames: ['Russia', 'Azerbaijan', 'Georgia'],
    category: 'skirmish',
    type: 'state-based',
    startYear: 1994,
    deathsCumulative: 50000,
    deaths2024: 10,
    deaths2025: 9,
    sideA: ['Russian security forces'],
    sideB: ['ISIS-Caucasus', 'Chechen/Dagestani militants'],
    region: 'Europe',
    description: 'Remnant jihadist insurgency in Dagestan, Chechnya, and the broader North Caucasus. Largely suppressed under Kadyrov\'s rule in Chechnya, but ISIS-linked cells persist.',
    status: 'Low-intensity — occasional militant attacks, heavy security presence',
  },
  {
    id: 'peru-shining-path',
    name: 'Peruvian Conflict',
    countries: ['PER'],
    countryNames: ['Peru'],
    category: 'skirmish',
    type: 'state-based',
    startYear: 1980,
    deathsCumulative: 70000,
    deaths2024: 45,
    deaths2025: 47,
    sideA: ['Peruvian military'],
    sideB: ['Shining Path (Militarizado)', 'Drug trafficking groups'],
    region: 'Americas',
    description: 'Residual Shining Path (Sendero Luminoso) insurgency in the VRAEM coca-growing region. The group now functions primarily as a drug trafficking organization with Maoist rhetoric.',
    status: 'Low-intensity — remnant Shining Path in VRAEM, coca economy',
  },
  {
    id: 'casamance',
    name: 'Casamance Conflict',
    countries: ['SEN', 'GMB'],
    countryNames: ['Senegal', 'Gambia'],
    category: 'skirmish',
    type: 'state-based',
    startYear: 1982,
    deathsCumulative: 5000,
    deaths2024: 5,
    deaths2025: 6,
    sideA: ['Senegalese military'],
    sideB: ['MFDC (Movement of Democratic Forces of Casamance)'],
    region: 'Africa',
    description: 'One of Africa\'s longest-running separatist conflicts. The MFDC seeks independence for the Casamance region. Violence has declined significantly but occasional incidents persist.',
    status: 'Low-intensity — mostly dormant, occasional incidents',
  },
  {
    id: 'el-salvador-crackdown',
    name: 'Salvadoran Gang Crackdown',
    countries: ['SLV'],
    countryNames: ['El Salvador'],
    category: 'skirmish',
    type: 'non-state',
    startYear: 2022,
    deathsCumulative: 545,
    deaths2024: 10,
    deaths2025: 10,
    sideA: ['Salvadoran security forces', 'Bukele government'],
    sideB: ['MS-13', 'Barrio 18 (remnants)'],
    region: 'Americas',
    description: 'President Bukele\'s state of exception and mass incarceration campaign has dramatically reduced gang violence but raised serious human rights concerns. Over 80,000 detained.',
    status: 'Low-intensity — gangs largely suppressed, mass detention continues',
  },
  {
    id: 'laos-insurgency',
    name: 'Laotian Hmong Insurgency',
    countries: ['LAO'],
    countryNames: ['Laos'],
    category: 'skirmish',
    type: 'state-based',
    startYear: 1975,
    deathsCumulative: 100000,
    deaths2024: 5,
    deaths2025: 5,
    sideA: ['Lao People\'s Army'],
    sideB: ['Hmong resistance remnants'],
    region: 'Asia',
    description: 'Remnant resistance from Hmong fighters who allied with the US during the Vietnam War. Now a very low-level insurgency with occasional reports of clashes in remote areas.',
    status: 'Mostly dormant — occasional reports of armed encounters',
  },
  {
    id: 'brazil-gangs',
    name: 'Brazilian Urban Violence',
    countries: ['BRA'],
    countryNames: ['Brazil'],
    category: 'conflict',
    type: 'non-state',
    startYear: 1990,
    deathsCumulative: 200000,
    deaths2024: 3000,
    deaths2025: 3000,
    sideA: ['Brazilian police (BOPE, CORE)', 'Military police'],
    sideB: ['Comando Vermelho', 'PCC (Primeiro Comando da Capital)', 'Milícias'],
    region: 'Americas',
    description: 'Chronic urban violence in Brazilian favelas and cities. Criminal factions control large areas. Police operations often result in deadly shootouts. Brazil has one of the world\'s highest homicide rates.',
    status: 'Active — gang territorial control, police operations, militia expansion',
  },
];

/** Pre-computed ISO3 → country name lookup */
export const iso3ToName: Record<string, string> = {};
for (const c of conflicts) {
  for (let i = 0; i < c.countries.length; i++) {
    iso3ToName[c.countries[i]] = c.countryNames[i];
  }
}
